/**
 * Message store using Zustand.
 * Implements Pattern 11 (Optimistic UI) - shows messages immediately before server confirms.
 * Integrates with upload queue for offline-first messaging.
 */
import { create } from 'zustand';
import type {
  Message,
  ConversationPreview,
  SendMessageData,
  OptimisticMessage,
  DisplayMessage,
} from '../types';
import { isOptimisticMessage } from '../types';
import { api } from '../api/client';
import { serializeOverlays } from '../utils/overlayPayload';
import { sendEngine } from '../services/queue/engine';
import { computeUploadTimeoutMs } from '../services/uploadPolicy';
import { viewedLedger } from '../services/viewedLedger';
import { receiveStore } from '../services/inbox/receiveStore';
import { useAuthStore } from './authStore';

// Generate unique temporary ID for optimistic messages
function generateTempId(): string {
  return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/** Delivery-receipt fields the server can advance after a message is sent. */
interface MessageStatusPatch {
  status?: Message['status'];
  viewed_at?: string | null;
  // Opening a moment starts its one-minute replay window; the replay button
  // reads this, so patching it keeps the countdown honest without a refetch.
  expires_at?: string | null;
}

interface MessageState {
  // State
  inbox: ConversationPreview[];
  conversations: Record<number, DisplayMessage[]>; // friend_id -> messages (real + optimistic)
  unreadCount: number;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchInbox: () => Promise<void>;
  fetchConversation: (friendId: number, beforeId?: number, silent?: boolean) => Promise<boolean>;
  receiveMessage: (message: Message) => void;
  applyMessageStatus: (messageId: number, patch: MessageStatusPatch) => void;
  addOptimisticMoment: (data: SendMessageData, currentUserId: number) => string;
  sendTextMessage: (recipientId: number, text: string, currentUserId: number) => Promise<boolean>;
  retryMessage: (tempId: string) => Promise<boolean>;
  dismissFailedMessage: (tempId: string, friendId: number) => void;
  markAsViewed: (messageId: number) => Promise<void>;
  noteViewedElsewhere: (messageId: number) => void;
  enableReplay: (messageId: number) => Promise<boolean>;
  fetchUnreadCount: () => Promise<void>;
  fetchUnreadSnaps: (friendId: number) => Promise<Message[]>;
  deleteConversation: (friendId: number) => Promise<boolean>;
  clearError: () => void;
}

export const useMessageStore = create<MessageState>((set, get) => ({
  // Initial state
  inbox: [],
  conversations: {},
  unreadCount: 0,
  isLoading: false,
  error: null,

  // Fetch inbox with conversation previews
  fetchInbox: async () => {
    set({ isLoading: true, error: null });

    const response = await api.get<ConversationPreview[]>('/messages/inbox');

    if (response.success && response.data) {
      set({ inbox: response.data, isLoading: false });
    } else {
      set({
        isLoading: false,
        error: response.error?.message || 'Failed to fetch inbox',
      });
    }
  },

  // Fetch messages in a conversation.
  // `silent` skips the loading flag — used for the background reconcile that
  // marks a live-delivered text as read, where a spinner would be a lie.
  fetchConversation: async (friendId: number, beforeId?: number, silent = false): Promise<boolean> => {
    if (!silent) {
      set({ isLoading: true, error: null });
    }

    const params: Record<string, string> = {};
    if (beforeId) {
      params.before = String(beforeId);
    }

    const response = await api.get<Message[]>(`/messages/conversation/${friendId}`, params);

    if (response.success && response.data) {
      set((state) => {
        const existingMessages = state.conversations[friendId] || [];

        // Keep optimistic messages (they have string IDs starting with "temp_")
        const optimisticMessages = existingMessages.filter(
          (m) => isOptimisticMessage(m)
        );

        // If loading more (has beforeId), append real messages to existing real ones
        // Otherwise, replace all real messages with fresh data
        const serverMessages = response.data as Message[];

        if (beforeId) {
          // Append older messages
          const existingReal = existingMessages.filter(
            (m) => !isOptimisticMessage(m)
          );
          return {
            conversations: {
              ...state.conversations,
              [friendId]: [...optimisticMessages, ...existingReal, ...serverMessages],
            },
            isLoading: false,
          };
        }

        // Fresh load - optimistic messages first, then server messages
        return {
          conversations: {
            ...state.conversations,
            [friendId]: [...optimisticMessages, ...serverMessages],
          },
          isLoading: false,
        };
      });

      return (response.data as Message[]).length === 20;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to fetch conversation',
    });
    return false;
  },

  /**
   * A message just arrived over the WebSocket — put it in the thread (BUG-043).
   *
   * Without this an open conversation was frozen: `new_message` refreshed the
   * inbox and stopped, so you had to leave the chat and come back to see what
   * someone had just sent you. The frame carries the whole message, so this
   * needs no round trip and doesn't disturb history the reader has scrolled back
   * through.
   *
   * The server only ever sends this frame to the recipient, so the thread it
   * belongs to is always the sender's.
   */
  receiveMessage: (message: Message) => {
    set((state) => {
      const friendId = message.sender_id;
      const existing = state.conversations[friendId] || [];

      // A reconnect can replay a frame we already have
      const alreadyHere = existing.some(
        (m) => !isOptimisticMessage(m) && m.id === message.id
      );
      if (alreadyHere) return state;

      return {
        conversations: {
          ...state.conversations,
          [friendId]: [message, ...existing], // newest first
        },
      };
    });
  },

  /**
   * Advance a sent message's receipt state from a `message_delivered` or
   * `message_viewed` frame, so the ticks under your own message move while you
   * are watching them rather than on the next refetch.
   */
  applyMessageStatus: (messageId: number, patch: MessageStatusPatch) => {
    set((state) => {
      const conversations = { ...state.conversations };
      for (const friendId in conversations) {
        conversations[friendId] = conversations[friendId].map((msg) =>
          !isOptimisticMessage(msg) && msg.id === messageId ? { ...msg, ...patch } : msg
        );
      }

      const inbox = state.inbox.map((c) =>
        c.last_message && c.last_message.id === messageId
          ? { ...c, last_message: { ...c.last_message, ...patch } }
          : c
      );

      return { conversations, inbox };
    });
  },

  /**
   * Show the moment in the thread immediately, and hand the sending itself to
   * the queue (FEAT-038 wave 2).
   *
   * There is no direct POST here any more. The old shape — try the network,
   * and only write the send down if that fails — is what BUG-119 was: a send
   * killed between the attempt and the fallback became a moment with no
   * recipient. The recipient is on a ledger row before this function is even
   * called; this is only the bubble.
   */
  addOptimisticMoment: (data: SendMessageData, currentUserId: number): string => {
    const tempId = generateTempId();
    const clientKey = data.client_key ?? crypto.randomUUID();
    const localMediaUrl = URL.createObjectURL(data.media);
    const localThumbnailUrl = data.thumbnail
      ? URL.createObjectURL(data.thumbnail)
      : null;

    const optimisticMessage: OptimisticMessage = {
      id: tempId,
      sender_id: currentUserId,
      recipient_id: data.recipient_id,
      message_type: 'snap',
      text_content: null,
      media_type: data.media_type,
      media_url: localMediaUrl,
      thumbnail_url: localThumbnailUrl,
      caption: data.caption || null,
      status: 'queued',
      retention_type: data.retention_type || '24h',
      expires_at: null,
      viewed_at: null,
      replay_allowed: data.replay_allowed ?? true,
      replayed_at: null,
      saved_to_vault: false,
      created_at: new Date().toISOString(),
      _isOptimistic: true,
      _localMediaBlob: data.media,
      _localOverlays: data.overlays ?? null,
      _localThumbnailBlob: data.thumbnail,
      _clientKey: clientKey,
    };

    set((state) => ({
      conversations: {
        ...state.conversations,
        [data.recipient_id]: [
          optimisticMessage,
          ...(state.conversations[data.recipient_id] || []),
        ],
      },
      error: null,
    }));

    return tempId;
  },

  // Retry a failed optimistic message
  retryMessage: async (tempId: string): Promise<boolean> => {
    // Find the failed message
    let failedMessage: OptimisticMessage | null = null;
    let friendId: number | null = null;

    const state = get();
    for (const [fId, messages] of Object.entries(state.conversations)) {
      const found = messages.find(
        (m) => isOptimisticMessage(m) && m.id === tempId
      );
      if (found && isOptimisticMessage(found)) {
        failedMessage = found;
        friendId = parseInt(fId, 10);
        break;
      }
    }

    if (!failedMessage || friendId === null) {
      console.error('Could not find failed message to retry:', tempId);
      return false;
    }

    // Update status to 'sending' again
    set((state) => {
      const messages = state.conversations[friendId!] || [];
      const updatedMessages = messages.map((msg) => {
        if (isOptimisticMessage(msg) && msg.id === tempId) {
          return {
            ...msg,
            status: 'sending' as const,
            _error: undefined,
          };
        }
        return msg;
      });

      return {
        conversations: {
          ...state.conversations,
          [friendId!]: updatedMessages,
        },
        error: null,
      };
    });

    let response: { success: boolean; data?: Message; error?: { code: string; message: string } };

    // Handle text messages
    if (failedMessage.message_type === 'text') {
      if (!failedMessage.text_content) {
        console.error('Cannot retry text message without content');
        return false;
      }

      // FEAT-024: replay the ORIGINAL key. If the first attempt actually
      // landed and only its answer was lost, the server hands that message
      // back instead of sending the text a second time.
      response = await api.post<Message>('/messages/text', {
        recipient_id: failedMessage.recipient_id,
        text: failedMessage.text_content,
        ...(failedMessage._clientKey ? { client_key: failedMessage._clientKey } : {}),
      });
    }
    // Handle snap/moment messages (photos/videos)
    else if (failedMessage.message_type === 'snap' && failedMessage.media_type && failedMessage._localMediaBlob) {
      // Build form data and retry
      const formData = new FormData();
      formData.append('recipient_id', String(failedMessage.recipient_id));
      formData.append('media_type', failedMessage.media_type);
      formData.append('media', failedMessage._localMediaBlob);

      if (failedMessage.caption) {
        formData.append('caption', failedMessage.caption);
      }
      formData.append('retention_type', failedMessage.retention_type);
      formData.append('replay_allowed', String(failedMessage.replay_allowed));
      // The decoration rides the request, not the bytes (FEAT-028 wave 2), so
      // a retry that forgot it would deliver the picture without the caption.
      const retryOverlays = serializeOverlays(failedMessage._localOverlays);
      if (retryOverlays) formData.append('overlays', retryOverlays);

      if (failedMessage._localThumbnailBlob) {
        formData.append('thumbnail', failedMessage._localThumbnailBlob);
      }
      if (failedMessage._clientKey) {
        formData.append('client_key', failedMessage._clientKey);
      }

      response = await api.upload<Message>(
        '/messages',
        formData,
        undefined,
        { timeoutMs: computeUploadTimeoutMs(failedMessage._localMediaBlob.size) }
      );
    } else {
      console.error('Cannot retry message - missing required data');
      return false;
    }

    if (response.success && response.data) {
      // Replace with real message
      const realMessage = response.data as Message;

      set((state) => {
        const messages = state.conversations[friendId!] || [];
        const updatedMessages = messages.map((msg) =>
          isOptimisticMessage(msg) && msg.id === tempId ? realMessage : msg
        );

        // Revoke blob URLs
        if (failedMessage!.media_url) {
          URL.revokeObjectURL(failedMessage!.media_url);
        }
        if (failedMessage!.thumbnail_url) {
          URL.revokeObjectURL(failedMessage!.thumbnail_url);
        }

        return {
          conversations: {
            ...state.conversations,
            [friendId!]: updatedMessages,
          },
        };
      });

      get().fetchInbox();
      return true;
    }

    // Still failed - update error
    set((state) => {
      const messages = state.conversations[friendId!] || [];
      const updatedMessages = messages.map((msg) => {
        if (isOptimisticMessage(msg) && msg.id === tempId) {
          return {
            ...msg,
            status: 'failed' as const,
            _error: response.error?.message || 'Failed to send message',
          };
        }
        return msg;
      });

      return {
        conversations: {
          ...state.conversations,
          [friendId!]: updatedMessages,
        },
        error: response.error?.message || 'Failed to send message',
      };
    });

    return false;
  },

  // Dismiss/remove a failed message
  dismissFailedMessage: (tempId: string, friendId: number) => {
    set((state) => {
      const messages = state.conversations[friendId] || [];

      // Find message to clean up blob URLs
      const failedMsg = messages.find(
        (m) => isOptimisticMessage(m) && m.id === tempId
      );
      if (failedMsg && isOptimisticMessage(failedMsg)) {
        if (failedMsg.media_url) {
          URL.revokeObjectURL(failedMsg.media_url);
        }
        if (failedMsg.thumbnail_url) {
          URL.revokeObjectURL(failedMsg.thumbnail_url);
        }
      }

      // Remove from conversation
      const updatedMessages = messages.filter(
        (m) => !(isOptimisticMessage(m) && m.id === tempId)
      );

      return {
        conversations: {
          ...state.conversations,
          [friendId]: updatedMessages,
        },
      };
    });
  },

  // Record that a moment was actually looked at.
  //
  // Downloading a moment is not looking at it: the viewer fetches the NEXT
  // moment while the current one is on screen, and this used to be a stub
  // because the download itself did the marking. So a moment could be
  // reported to its sender as opened — and deleted a minute later — without
  // anyone seeing it. Bytes now come from /messages/{id}/media, which changes
  // nothing, and only what is displayed calls this.
  //
  // Idempotent server-side, so a repeat call is harmless. A failure is
  // deliberately silent: it costs a receipt, never the moment on screen.
  markAsViewed: async (messageId: number) => {
    // Recorded BEFORE the network: the moment was on screen — that is the
    // fact, and it must hold even offline. The ledger is what keeps a stale
    // cached unread list from replaying this moment on a cold launch
    // (BUG-117).
    viewedLedger.markViewed(messageId);
    // Viewed = the offline inbox's bytes leave the phone (FEAT-040 w3,
    // his rule verbatim: "bytes on the phone until viewed"). Before the
    // network for the same reason the ledger is, and on EVERY retention
    // type — the in-run back-nav copy lives in the viewer's memory, and a
    // replay's bytes only ever come from the consuming POST.
    void receiveStore.shred(messageId).catch(() => {});
    const response = await api.post<Message>(`/messages/${messageId}/view-mark`);

    if (response.success && response.data) {
      const { status, viewed_at, expires_at } = response.data;
      get().applyMessageStatus(messageId, { status, viewed_at, expires_at });
    }
  },

  // FEAT-040 w3: another of THIS user's devices viewed the moment. Its bytes
  // leave the offline inbox now, and the ledger entry keeps a stale cached
  // unread list from ever replaying it here (the BUG-117 rule, cross-device).
  // No server call — the viewing device already made it.
  noteViewedElsewhere: (messageId: number) => {
    viewedLedger.markViewed(messageId);
    void receiveStore.shred(messageId).catch(() => {});
  },

  // Enable replay for a viewed message (long-press action)
  enableReplay: async (messageId: number): Promise<boolean> => {
    const response = await api.post<Message>(`/messages/${messageId}/enable-replay`);

    if (response.success && response.data) {
      // Update message in conversations to show replay is enabled
      set((state) => {
        const newConversations = { ...state.conversations };
        const newInbox = [...state.inbox];

        // Update in conversations
        for (const friendId in newConversations) {
          newConversations[friendId] = newConversations[friendId].map((msg) => {
            if (!isOptimisticMessage(msg) && msg.id === messageId) {
              return { ...msg, replay_enabled_at: response.data!.replay_enabled_at };
            }
            return msg;
          });
        }

        // Update in inbox
        for (let i = 0; i < newInbox.length; i++) {
          if (newInbox[i].last_message && newInbox[i].last_message!.id === messageId) {
            newInbox[i] = {
              ...newInbox[i],
              last_message: {
                ...newInbox[i].last_message!,
                replay_enabled_at: response.data!.replay_enabled_at,
              },
            };
          }
        }

        return { conversations: newConversations, inbox: newInbox };
      });

      return true;
    }

    // Don't set global error - just return false and let caller handle it
    console.error('Failed to enable replay:', response.error?.message);
    return false;
  },

  // BUG-106: a `saveToVault` action used to live here. It posted to
  // /messages/{id}/save — a route the backend has never had — and nothing
  // called it. The real save is POST /vault/items, which the viewer's save
  // button does directly. (storyStore has its own saveToVault; that one IS
  // called and /stories/{id}/save does exist. Different action, leave it.)

  // Fetch unread count
  fetchUnreadCount: async () => {
    const response = await api.get<{ count: number }>('/messages/unread-count');

    if (response.success && response.data) {
      set({ unreadCount: response.data.count });
    }
  },

  // Send a text message
  sendTextMessage: async (recipientId: number, text: string, currentUserId: number): Promise<boolean> => {
    // Generate temp ID for optimistic update
    const tempId = generateTempId();
    // FEAT-024: this text's identity, minted once. A retry replays the SAME
    // key, so a send that landed but lost its answer converges on the server
    // instead of arriving twice.
    const clientKey = `t-${crypto.randomUUID()}`;

    // Create optimistic message
    const optimisticMessage: OptimisticMessage = {
      id: tempId,
      _clientKey: clientKey,
      sender_id: currentUserId,
      recipient_id: recipientId,
      message_type: 'text',
      text_content: text,
      media_type: null,
      media_url: null,
      thumbnail_url: null,
      caption: null,
      status: 'sending',
      retention_type: '24h',
      expires_at: null,
      viewed_at: null,
      replay_allowed: false,
      replayed_at: null,
      saved_to_vault: false,
      created_at: new Date().toISOString(),
      _isOptimistic: true,
    };

    // Add to conversation immediately
    set((state) => ({
      conversations: {
        ...state.conversations,
        [recipientId]: [optimisticMessage, ...(state.conversations[recipientId] || [])],
      },
      error: null,
    }));

    // Send to server
    const response = await api.post<Message>('/messages/text', {
      recipient_id: recipientId,
      text,
      client_key: clientKey,
    });

    if (response.success && response.data) {
      // Replace optimistic with real message
      const realMessage = response.data as Message;

      set((state) => ({
        conversations: {
          ...state.conversations,
          [recipientId]: (state.conversations[recipientId] || []).map((msg) =>
            isOptimisticMessage(msg) && msg.id === tempId ? realMessage : msg
          ),
        },
      }));

      // Refresh inbox
      get().fetchInbox();
      return true;
    }

    // Mark as failed
    set((state) => ({
      conversations: {
        ...state.conversations,
        [recipientId]: (state.conversations[recipientId] || []).map((msg) => {
          if (isOptimisticMessage(msg) && msg.id === tempId) {
            return { ...msg, status: 'failed' as const, _error: response.error?.message };
          }
          return msg;
        }),
      },
      error: response.error?.message || 'Failed to send message',
    }));

    return false;
  },

  // Fetch unread snaps from a specific friend.
  //
  // Filtered through the viewed ledger (BUG-117): a list fetched moments ago
  // can still carry a moment this device just opened, and it must not go
  // back in the play queue — the server would even re-serve its bytes inside
  // the post-view window. (The unread route is no-store since FEAT-040 w3,
  // so the stale-SW-cache half of that hazard is gone; the ledger stays for
  // the in-flight and offline halves.)
  fetchUnreadSnaps: async (friendId: number): Promise<Message[]> => {
    const response = await api.get<Message[]>(`/messages/conversation/${friendId}/unread`);

    if (response.success && response.data) {
      return response.data.filter((m) => !viewedLedger.hasViewed(m.id));
    }

    // No answer — offline, most often. The offline inbox IS the unread list
    // now (FEAT-040 w3): stored-only (pending rows have no bytes to show),
    // oldest-first, the same ledger filter. Server truth wins unchanged the
    // moment it answers again.
    const userId = useAuthStore.getState().user?.id;
    if (userId === undefined) return [];
    try {
      await receiveStore.whenReady();
    } catch {
      return [];
    }
    return receiveStore
      .envelopesFor(userId, friendId)
      .filter((m) => !viewedLedger.hasViewed(m.id));
  },

  // Delete entire conversation
  deleteConversation: async (friendId: number): Promise<boolean> => {
    // Optimistic: Remove from inbox immediately
    set((state) => ({
      inbox: state.inbox.filter((c) => c.friend_id !== friendId),
      conversations: {
        ...state.conversations,
        [friendId]: [],
      },
    }));

    const response = await api.delete<{ deleted_count: number }>(`/messages/conversation/${friendId}`);

    if (response.success) {
      return true;
    }

    // Restore on failure
    get().fetchInbox();
    set({ error: response.error?.message || 'Failed to delete conversation' });
    return false;
  },

  // Clear error
  clearError: () => set({ error: null }),
}));

// Queue outcomes drive the queued bubbles: a success replaces them with the
// server row, a permanent refusal marks them failed. A row going 'stuck'
// changes nothing here — the moment is still on the phone and the pill owns
// the retry/discard decision.
sendEngine.onResult((result) => {
  const { row } = result;
  if (row.dest.kind !== 'friend') return;

  const state = useMessageStore.getState();
  const friendId = row.dest.friendId;
  const messages = state.conversations[friendId] || [];

  const matches = (msg: OptimisticMessage) =>
    row.optimisticId === msg.id || msg._clientKey === row.clientKey;

  const updatedMessages = messages.map((msg) => {
    if (!isOptimisticMessage(msg) || !matches(msg)) return msg;
    if (result.success) return result.server as Message;
    if (result.permanent) {
      return { ...msg, status: 'failed' as const, _error: result.error };
    }
    return msg;
  });

  useMessageStore.setState({
    conversations: {
      ...state.conversations,
      [friendId]: updatedMessages,
    },
  });

  if (result.success) {
    // Refresh inbox to show updated last message
    useMessageStore.getState().fetchInbox();
  }
});
